/**
 * Version Details Dialog for Home Page
 * Shows version changelog and marks as seen
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Calendar,
  Check,
  ChevronLeft,
  ChevronRight,
  Code2,
  Download,
  Eye,
  History,
  Info,
  Play,
  Shield,
  Tag,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getVersionDetailsAuth,
  type AppVersionDetails,
} from '@/services/appVersions.api';
import { markVersionSeen } from '@/routes/api/main-api';
import { useAuth } from '@/store';
import { toast } from 'sonner';

interface VersionDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: string;
}

export const VersionDetailsDialog = ({
  open,
  onOpenChange,
  version,
}: VersionDetailsDialogProps) => {
  const [versionData, setVersionData] = useState<AppVersionDetails | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSeen, setHasSeen] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);
  const [allMedia, setAllMedia] = useState<string[]>([]);
  const [mediaMetadata, setMediaMetadata] = useState<Array<{
    path: string;
    title: string;
    type: 'fix' | 'new feature';
  }>>([]);
  
  const { user } = useAuth();
  const userId = user?._id || 'me';
  
  // Get server base URL for constructing image URLs
  const serverBaseUrl = import.meta.env.VITE_HEALTHY_EMPIRE_SERVER || '';
  
  // Helper function to get full image URL
  const getImageUrl = (path: string): string => {
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${serverBaseUrl}/${cleanPath}`;
  };
  
  // Helper to determine if file is video
  const isVideo = (path: string): boolean => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    return videoExtensions.some((ext) => path.toLowerCase().endsWith(ext));
  };
  
  // Collect all media from changelog with metadata
  useEffect(() => {
    if (versionData?.changelog) {
      const media: string[] = [];
      const metadata: Array<{
        path: string;
        title: string;
        type: 'fix' | 'new feature';
      }> = [];
      
      versionData.changelog.forEach((item) => {
        if (item.media && item.media.length > 0) {
          item.media.forEach(mediaPath => {
            media.push(mediaPath);
            metadata.push({
              path: mediaPath,
              title: item.title,
              type: item.type,
            });
          });
        }
      });
      setAllMedia(media);
      setMediaMetadata(metadata);
    }
  }, [versionData]);
  
  // Carousel navigation
  const goToNext = useCallback(() => {
    if (selectedMediaIndex !== null && allMedia.length > 0) {
      setSelectedMediaIndex((selectedMediaIndex + 1) % allMedia.length);
    }
  }, [selectedMediaIndex, allMedia.length]);
  
  const goToPrevious = useCallback(() => {
    if (selectedMediaIndex !== null && allMedia.length > 0) {
      setSelectedMediaIndex(
        selectedMediaIndex === 0 ? allMedia.length - 1 : selectedMediaIndex - 1,
      );
    }
  }, [selectedMediaIndex, allMedia.length]);
  
  // Keyboard navigation for carousel
  useEffect(() => {
    if (selectedMediaIndex === null) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') setSelectedMediaIndex(null);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMediaIndex, goToNext, goToPrevious]);
  
  // Fetch version details
  useEffect(() => {
    if (open && version) {
      fetchVersionDetails();
    } else {
      // Reset state when dialog closes
      setVersionData(null);
      setError(null);
      setHasSeen(false);
      setSelectedMediaIndex(null);
      setAllMedia([]);
      setMediaMetadata([]);
    }
  }, [open, version]);
  
  const fetchVersionDetails = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getVersionDetailsAuth(version);
      
      if (response.success && response.data) {
        setVersionData(response.data);
        
        // Mark as seen after successfully loading
        if (!hasSeen) {
          await markVersionSeen({ userId, version });
          setHasSeen(true);
        }
      } else {
        throw new Error(response.message || 'فشل في تحميل تفاصيل الإصدار');
      }
    } catch (error) {
      console.error('Error loading version details:', error);
      const errorMessage = error instanceof Error ? error.message : 'فشل في تحميل تفاصيل الإصدار';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="h-5 w-5 text-blue-600" />
            الإصدار {version || '...'}
          </DialogTitle>
          <DialogDescription>
            تفاصيل الإصدار والتحديثات المتضمنة
          </DialogDescription>
          {hasSeen && (
            <Badge variant="outline" className="text-xs mt-2">
              <Check className="h-3 w-3 ml-1" />
              تم الاطلاع
            </Badge>
          )}
        </DialogHeader>
        
        <div className="max-h-[calc(90vh-120px)] overflow-y-auto px-1">
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : error ? (
            <div className="py-8 text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
                <Info className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                حدث خطأ
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {error}
              </p>
            </div>
          ) : versionData ? (
            <div className="space-y-6">
              {/* Version Info */}
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    رقم الإصدار
                  </label>
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4" />
                    <span className="font-mono">{versionData.version}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    معرف البناء
                  </label>
                  <div className="flex items-center gap-2">
                    <Code2 className="h-4 w-4" />
                    <span className="font-mono text-sm">
                      {versionData.buildId}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    تاريخ النشر
                  </label>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {new Date(versionData.publishedAt).toLocaleString('ar-EG', {
                        calendar: 'gregory',
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    نوع التحديث
                  </label>
                  <div className="flex items-center gap-2">
                    {versionData.isForced ? (
                      <>
                        <Shield className="h-4 w-4 text-red-500" />
                        <Badge
                          variant="soft"
                          className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
                        >
                          إجباري
                        </Badge>
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 text-green-500" />
                        <Badge
                          variant="soft"
                          className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                        >
                          اختياري
                        </Badge>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Changelog */}
              <div className="space-y-4">
                <h3 className="flex items-center gap-2 font-semibold">
                  <History className="h-4 w-4" />
                  سجل التغييرات
                </h3>
                
                {versionData.changelog && versionData.changelog.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {versionData.changelog.map((item, index) => (
                      <div
                        key={index}
                        className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50"
                      >
                        {/* Header with badge */}
                        <div className="flex items-start justify-between">
                          <Badge
                            variant="outline"
                            className={`flex items-center gap-1.5 ${
                              item.type === 'fix'
                                ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
                                : 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
                            }`}
                          >
                            <div
                              className={`h-2 w-2 rounded-full ${
                                item.type === 'fix'
                                  ? 'bg-red-500'
                                  : 'bg-green-500'
                              }`}
                            />
                            {item.type === 'fix' ? 'إصلاح' : 'ميزة جديدة'}
                          </Badge>
                        </div>
                        
                        {/* Title */}
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          {item.title}
                        </p>
                        
                        {/* Media */}
                        {item.media && item.media.length > 0 && (
                          <div className="mt-auto space-y-2">
                            <div className={`grid gap-2 ${item.media.length === 1 ? 'grid-cols-1' : 'grid-cols-2 lg:grid-cols-3'}`}>
                              {item.media.map((mediaPath, mediaIndex) => {
                                const isVideoFile = isVideo(mediaPath);
                                const actualIndex = allMedia.findIndex(m => m === mediaPath);
                                
                                return (
                                  <div
                                    key={mediaIndex}
                                    className="group relative aspect-video overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800 cursor-pointer hover:shadow-lg transition-all duration-200"
                                    onClick={() => {
                                      if (actualIndex !== -1) {
                                        setSelectedMediaIndex(actualIndex);
                                      }
                                    }}
                                  >
                                    {isVideoFile ? (
                                      <>
                                        <video
                                          src={getImageUrl(mediaPath)}
                                          className="h-full w-full object-cover"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors pointer-events-none">
                                          <Play className="h-8 w-8 text-white" />
                                        </div>
                                      </>
                                    ) : (
                                      <img
                                        src={getImageUrl(mediaPath)}
                                        alt={`${item.title} - ${mediaIndex + 1}`}
                                        className="h-full w-full object-cover transition-transform duration-200 hover:scale-110"
                                        loading="lazy"
                                        onError={(e) => {
                                          const target = e.currentTarget;
                                          target.style.display = 'none';
                                          const placeholder = document.createElement('div');
                                          placeholder.className = 'flex h-full w-full items-center justify-center bg-gray-200 dark:bg-gray-700';
                                          placeholder.innerHTML = `<svg class="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>`;
                                          target.parentElement?.appendChild(placeholder);
                                        }}
                                      />
                                    )}
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none">
                                      <Eye className="h-5 w-5 text-white" />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500 dark:text-gray-400">
                    <Info className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    <p>لا توجد تفاصيل متاحة لهذا الإصدار</p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
    
    {/* Media Viewer Dialog */}
    <Dialog
      open={selectedMediaIndex !== null && allMedia.length > 0}
      onOpenChange={(open) => {
        if (!open) {
          setSelectedMediaIndex(null);
        }
      }}
    >
      <DialogContent className="max-h-[95vh] max-w-[95vw] overflow-hidden border-none bg-black/95 p-0">
        <div className="relative flex h-[90vh] w-full items-center justify-center">
          {/* Previous Button */}
          {allMedia.length > 1 && selectedMediaIndex !== null && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
              aria-label="Previous"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          
          {/* Media Content */}
          <div className="relative flex max-h-[85vh] max-w-full items-center justify-center">
            {selectedMediaIndex !== null &&
              selectedMediaIndex < allMedia.length &&
              (isVideo(allMedia[selectedMediaIndex]) ? (
                <video
                  key={selectedMediaIndex}
                  src={getImageUrl(allMedia[selectedMediaIndex])}
                  controls
                  autoPlay
                  className="max-h-[85vh] max-w-full rounded-lg"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <img
                  src={getImageUrl(allMedia[selectedMediaIndex])}
                  alt={`عرض ${selectedMediaIndex + 1} من ${allMedia.length}`}
                  className="max-h-[85vh] max-w-full rounded-lg object-contain"
                  onClick={(e) => e.stopPropagation()}
                  onError={(e) => {
                    const target = e.currentTarget as HTMLImageElement;
                    target.src =
                      'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iIzMzMyIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjIwMCIgeT0iMTUwIiBzdHlsZT0iZmlsbDojYWFhO2ZvbnQtd2VpZ2h0OmJvbGQ7Zm9udC1zaXplOjE5cHg7Zm9udC1mYW1pbHk6QXJpYWwsSGVsdmV0aWNhLHNhbnMtc2VyaWY7ZG9taW5hbnQtYmFzZWxpbmU6Y2VudHJhbCI+Image not found</dGV4dD48L3N2Zz4=';
                  }}
                />
              ))}
          </div>
          
          {/* Next Button */}
          {allMedia.length > 1 && selectedMediaIndex !== null && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
              aria-label="Next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
          
          {/* Media Info and Counter */}
          {selectedMediaIndex !== null && mediaMetadata[selectedMediaIndex] && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-center">
              <div className="rounded-lg bg-black/70 px-4 py-2 backdrop-blur-sm">
                <div className="flex items-center gap-2 text-white mb-1">
                  <Badge
                    variant="outline"
                    className={`border-0 text-xs ${
                      mediaMetadata[selectedMediaIndex].type === 'fix'
                        ? 'bg-red-500/20 text-red-300'
                        : 'bg-green-500/20 text-green-300'
                    }`}
                  >
                    {mediaMetadata[selectedMediaIndex].type === 'fix' ? 'إصلاح' : 'ميزة جديدة'}
                  </Badge>
                  <span className="text-sm font-medium">
                    {mediaMetadata[selectedMediaIndex].title}
                  </span>
                </div>
                {allMedia.length > 1 && (
                  <div className="text-xs text-gray-300">
                    {selectedMediaIndex + 1} / {allMedia.length}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};